import { Link, type LinkProps } from 'react-router-dom'
import { cn } from '@/internationalization/cn'
import type { ButtonSize, ButtonVariant } from './Button'

export interface LinkButtonProps extends LinkProps {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
}

/**
 * A navigation that looks like a button.
 *
 * It exists so nobody nests a <Link> inside a <Button>: that is invalid HTML,
 * it gives the element two conflicting roles, and middle-click / open-in-new-tab
 * stop working. Rule of thumb — if it changes the URL it is a link, if it
 * changes state it is a Button. The classes are deliberately the same strings as
 * Button's, so the two cannot drift apart visually.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-fg hover:bg-brand-hover shadow-xs',
  secondary: 'bg-surface text-fg border border-border hover:bg-surface-muted shadow-xs',
  ghost: 'text-fg-muted hover:bg-surface-muted hover:text-fg',
  danger: 'bg-danger text-white hover:brightness-95 shadow-xs',
  link: 'text-brand underline-offset-4 hover:underline px-0',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-sm gap-1.5 rounded',
  md: 'h-10 px-3.5 text-base gap-2 rounded-md',
  lg: 'h-11 px-5 text-md gap-2 rounded-md',
}

export function LinkButton({
  variant = 'secondary',
  size = 'md',
  fullWidth,
  className,
  ...props
}: LinkButtonProps) {
  return (
    <Link
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium',
        'transition-colors duration-fast',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    />
  )
}
